import React from 'react';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { Router } from 'react-router';
import { change } from 'redux-form';
import {
    syncHistoryWithStore,
    routerMiddleware,
    replace,
} from 'react-router-redux';
import isEmpty from 'lodash/isEmpty';
import { configureStore } from '@eb/redux-configure-store';
import { getHistory, updateHistory } from '@eb/react-router-history';
import {
    middleware as analyticsMiddleware,
    GA_SETTINGS_PROP_TYPE,
} from '@eb/site-analytics';
import { HAS_WINDOW } from '@eb/feature-detection';
import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eb/social-login-facebook';

import { rootReducer as reducer } from './common/reducers/root';
import getRoutes from './page/routes';
import getNewRoutes from './page/newRoutes';
import {
    BASE_URL,
    CHECKOUT_PATH,
    LOOK_UP_FORM,
    SET_PASSWORD_FORM,
    STLD_DISABLED,
    STLD_ENABLED,
} from './common/constants';

/**** DO NOT COPY THIS PATTERN ****/
//This is needed so that if the user begins to type before the JS
//has finished loading we don't want the field to be cleared, instead
//we want the form to be initialized with that value so the user
//has a seamless experience, but is something we are going to be moving away from
//and will be refactored in the near future.
const _initializeFieldValues = (store) => {
    const emailField = document.getElementById('email');
    const passwordField = document.getElementById('set-password');
    let fieldValues = [];
    let form = LOOK_UP_FORM;

    if (emailField) {
        fieldValues = ['email', emailField.value];
    } else if (passwordField) {
        fieldValues = ['password', passwordField.value];
        form = SET_PASSWORD_FORM;
    }

    //Call change here since the onEnter hooks instantiate
    //the form and so to prevent overwriting the values we just
    //want to update the formField we care about.
    store.dispatch(change(form, ...fieldValues));
};

export default class AuthenticationApp extends React.Component {
    static propTypes = {
        gaSettings: GA_SETTINGS_PROP_TYPE,
    };

    constructor(props) {
        super(props);

        const historyManagement = getHistory();
        const initialData = this._getInitialData();

        this._store = configureStore({
            reducer,
            middleware: [
                thunk,
                routerMiddleware(historyManagement),
                analyticsMiddleware(this.props),
            ],
            initialState: initialData,
        });
        this._history = syncHistoryWithStore(historyManagement, this._store);

        updateHistory(this._history, props.request);
    }

    componentDidMount() {
        const { featureFlags, isCheckoutLoginPath } = this.props;
        const isInNewFlow =
            featureFlags.launchAuthenticationRefactor && !isCheckoutLoginPath;

        if (HAS_WINDOW && !isInNewFlow) {
            _initializeFieldValues(this._store);
        }
    }

    componentWillReceiveProps(newProps) {
        updateHistory(this._history, newProps.request);
    }

    // Used for development purposes
    _getMockedDataAppleLogin(socialSSOSettings) {
        return {
            ...socialSSOSettings,
            ['apple']: {
                scope: 'name%20email',
                clientId: '6Q9LU82ADJ.com.eventbrite.attendee',
                redirectURI: 'https://eventbrite.com/appleAuth',
            },
        };
    }

    _getSocialData(socialAuthenticationData) {
        let socialData = {};

        if (
            socialAuthenticationData &&
            socialAuthenticationData.apple &&
            socialAuthenticationData.apple.accessToken
        ) {
            socialData = {
                accessToken: '',
                sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
                email: '',
                firstName: '',
                lastName: '',
            };
        }

        return socialData;
    }

    _getInitialData() {
        const {
            request,
            env,
            user,
            fp,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            gaSettings,
            socialSSOSettings,
            featureFlags,
            userHasAccount,
            shouldIncludeEmailConfirmation,
            socialAuthenticationData,
            isCheckoutRequest,
            socialDataMock,
        } = this.props;

        // TODO Remove this workaround once we are able to release this app to prod, and change the key name from the backend instead
        if (!isEmpty(socialAuthenticationData)) {
            socialAuthenticationData['accessToken'] =
                socialAuthenticationData['idToken'];
            delete socialAuthenticationData['idToken'];
        }
        return {
            gaSettings,
            isCheckoutRequest,
            socialDataMock: socialDataMock || {},

            authentication: {
                referrer: request.params.referrer || '',
                locale: env.localeInfo.locale,
                userTypeParams: {
                    userType: request.params['user_type'],
                    userTypeSig: request.params['user_type_sig'],
                },
                forward: request.params.forward || '',
                stld: request.params.stld || '',
                pckg: request.params.pckg || '',
                socialData: this._getSocialData(socialAuthenticationData),
                isAuthenticated: user.isAuthenticated,
                recaptcha2PublicKey,
                recaptcha3PublicKey,
                fp,
                socialSSOSettings,
                socialAuthenticationData: {
                    apple: socialAuthenticationData || {},
                    facebook: {},
                    google: {},
                },
                shouldIncludeEmailConfirmation,
                showCreatorEncouragement:
                    featureFlags && featureFlags.showCreatorEncouragement
                        ? featureFlags.showCreatorEncouragement
                        : false,
                disableRecaptcha3:
                    featureFlags && featureFlags.disableRecaptcha3
                        ? featureFlags.disableRecaptcha3
                        : false,
                featureFlags,
                userHasAccount,
            },
        };
    }

    render() {
        const {
            env,
            footerLinks,
            request,
            user,
            featureFlags,
            isCheckoutRequest,
        } = this.props;
        const checkoutPath = `${BASE_URL}/${CHECKOUT_PATH}`;
        const isCheckoutLoginPath = request.path === checkoutPath;

        const isinvalidCheckoutRequest =
            isCheckoutLoginPath && !isCheckoutRequest;
        isinvalidCheckoutRequest && this._store.dispatch(replace(BASE_URL));

        let routes = getRoutes(
            {
                env,
                footerLinks,
                request,
                user,
            },
            this._store,
        );

        if (featureFlags.launchAuthenticationRefactor && !isCheckoutLoginPath) {
            console.log('%c Welcome!', 'color: #3a3a3a; font-size: 22px;');
            console.log(
                '%c You are using the new code for authentication.',
                'color: #3a3a3a; font-size: 16px;',
            );

            routes = getNewRoutes(
                {
                    env,
                    footerLinks,
                    request,
                    user,
                },
                this._store,
            );
        }

        return (
            <Provider store={this._store}>
                <Router history={this._history} routes={routes} />
            </Provider>
        );
    }
}
